import { getUserDetails } from '../admin/helper/SessionHelper'
import { getActiveShopId } from '../shop/activeShop'

/**
 * Who the stored data belongs to (T-020).
 *
 * Several shops run off ONE deployment. Any client-side cache that is not
 * scoped can surface one shop's data inside another shop's session — a
 * half-finished invoice, a product list, a customer's outstanding balance. That
 * is the single worst failure mode this system has, and it is silent: nothing
 * errors, the wrong numbers simply appear and look plausible.
 *
 * So every key gets a scope, and the scope goes in FIRST — before the caching
 * work it protects. Retrofitting a namespace onto keys already in the field
 * means either a migration or a silent mix-up.
 */

/**
 * ✅ BE-02 IS ANSWERED, and the answer moved this function somewhere else.
 *
 * This used to try seven field names on the login response — `shop_id`,
 * `shopId`, `shop`, `tenant_id`, `tenantId`, `branch_id`, `branchId` — hoping
 * one of them carried the shop. None of them ever did. The backend has now
 * confirmed why: **the shop is not a property of the user at all.** One account
 * can work in several shops (BE-02b), so there is nothing on the user to read.
 *
 * The active shop is a choice, held in shop/activeShop.js and sent to the API
 * as `X-Shop-Id`. Scoping now follows that same choice, which means a
 * salesperson who switches shops mid-shift gets a different draft bucket from
 * the moment they switch — exactly the isolation this module was written for,
 * and which the guesswork above never actually delivered.
 *
 * 'unknown' survives as the fallback for one real case: a single-shop user
 * before GET /shops has answered, where no id is stored because none is needed.
 * They are the only occupant of that bucket, so it isolates nothing but it
 * mixes nothing either. unknownShop() stays exported so a caller can tell.
 */
export function getShopId() {
    const shopId = getActiveShopId()
    return shopId === null || shopId === '' ? 'unknown' : String(shopId)
}

export function getUserId() {
    let details
    try {
        details = getUserDetails()
    } catch (e) {
        return 'anonymous'
    }

    const id = details?.id ?? details?.user_id ?? details?.userId
    // An absent user id gets its own bucket rather than sharing one. Losing a
    // draft is bad; writing one salesperson's order into another's slot on a
    // shared counter PC is worse.
    return id === undefined || id === null || id === '' ? 'anonymous' : String(id)
}

/** True when the shop could not be identified — see the warning above. */
export function unknownShop() {
    return getShopId() === 'unknown'
}

/**
 * The namespace every scoped key carries: `shop:<id>:user:<id>`.
 *
 * Both parts, always. Shop alone would let two salespeople on the same counter
 * PC inherit each other's drafts; user alone would let the same person's data
 * follow them between shops.
 */
export function currentScope() {
    return `shop:${getShopId()}:user:${getUserId()}`
}

export function scopedKey(name) {
    if (!name) throw new Error('scopedKey needs a name')
    return `${currentScope()}:${name}`
}
